import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BundlePackage.css';
import { useUserAuth } from '../../context/UserAuthContext';
import dayjs from 'dayjs';
import { Modal, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import PackageDeal from './PackageDeal';

const PackageEnquiry = () => {
    const { user, leadBasePriceFinder, token } = useUserAuth();
    const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
    const merchant_id = user?.uid;
    const [inputCode, setInputCode] = useState('');

    // State management
    const [packageList, setPackageList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredPackages, setFilteredPackages] = useState([]);
    const [currentPaymentStatus, setCurrentPaymentStatus] = useState(false);
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const [merchant, setMerchant] = useState({});
    const [leadAmount, setLeadAmount] = useState(0);
    const [promoCodeList, setPromoCodeList] = useState([]);
    const [promoCode, setPromoCode] = useState({});
    const [code, setCode] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [loading, setLoading] = useState({
        merchant: false,
        promoCode: false,
        payment: false,
        table: false
    });
    const [showContactModal, setShowContactModal] = useState(false);
    const [currentContact, setCurrentContact] = useState({});
    console.log('current contact', currentContact)
    // Memoized calculations
    const { cgst, sgst, grandTotal } = useMemo(() => {
        const cgst = leadAmount * 0.09;
        const sgst = leadAmount * 0.09;
        const grandTotal = Math.round(Number(leadAmount) + cgst + sgst - discountAmount);
        return { cgst, sgst, grandTotal };
    }, [leadAmount, discountAmount]);

    // Helper functions
    const maskString = (str) => {
        if (!str) return '';
        if (str.length <= 2) return str;
        return 'X'.repeat(str.length - 2) + str.slice(-2);
    };

    // Data fetching
    const fetchPackageList = useCallback(async () => {
        0
        if (!merchant_id) return;
        try {
            setLoading(prev => ({ ...prev, table: true }));
            const res = await axios.get(`${base_url}/api/v1/packages/enquiry/vendor/${merchant_id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            setPackageList(res.data?.data || []);
        } catch (error) {
            toast.error(`Error fetching package list: ${error.message}`);
        } finally {
            setLoading(prev => ({ ...prev, table: false }));
        }
    }, [merchant_id]);

    const fetchMerchantData = useCallback(async () => {
        if (!merchant_id) return;
        setLoading(prev => ({ ...prev, merchant: true }));
        try {
            const res = await axios.get(`${base_url}/api/v1/users/id/${merchant_id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            setMerchant(res.data.data);
            const amount = await leadBasePriceFinder(res.data.data?.sub_category_id);
            setLeadAmount(Number(amount) || 0);
        } catch (error) {
            toast.error(`Error fetching merchant data: ${error.message}`);
        } finally {
            setLoading(prev => ({ ...prev, merchant: false }));
        }
    }, [merchant_id, leadBasePriceFinder, token]);

    const fetchPromoCodes = useCallback(async () => {
        setLoading(prev => ({ ...prev, promoCode: true }));
        try {
            const res = await axios.get(`${base_url}/api/v1/home/promo-code`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            console.log('promo', res.data)
            setPromoCodeList(res.data.data || []);
        } catch (error) {
            toast.error(`Error fetching promo codes: ${error.message}`);
        } finally {
            setLoading(prev => ({ ...prev, promoCode: false }));
        }
    }, [token]);

    // Initial data load
    useEffect(() => {
        fetchPackageList();
        fetchMerchantData();
        fetchPromoCodes();
    }, [fetchPackageList, fetchMerchantData, fetchPromoCodes]);

    // Filter packages based on search query
    useEffect(() => {
        const filtered = packageList.filter(item =>
            item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.package_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.venue_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredPackages(filtered);
    }, [searchQuery, packageList]);

    // Promo code validation and application
    const validatePromoCode = useCallback(async (code) => {
        if (!code) {
            toast.error('Please enter a promo code');
            return false;
        }
        setLoading(prev => ({ ...prev, promoCode: true }));
        try {
            const res = await axios.post(`${base_url}/api/v1/promo_codes/exist`, { code });
            const historyRes = await axios.post(`${base_url}/api/v1/promo-code-histories/history`, {
                promo_code_id: res.data.data.id,
                merchant_id
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (res.data.data.per_user_used > historyRes.data.usageCount) {
                return res.data.data;
            }
            toast.warning("You've exceeded the usage limit for this promo code");
            return null;
        } catch (error) {
            toast.error('Invalid promo code');
            return null;
        } finally {
            setLoading(prev => ({ ...prev, promoCode: false }));
        }
    }, [base_url, merchant_id, token]);

    const applyPromoCode = useCallback(async (code) => {
        setCode(code);
        const validCode = await validatePromoCode(code);
        if (!validCode) {
            setDiscountAmount(0);
            return;
        }
        setPromoCode(validCode);
        setInputCode('')
        const discount = (leadAmount + cgst + sgst) * validCode.value / 100;
        setDiscountAmount(discount);

    }, [validatePromoCode, leadAmount, cgst, sgst]);

    // Payment processing
    const processPayment = useCallback(async () => {
        if (!currentRequestId) {
            toast.error('No request selected for payment');
            return;
        }

        setLoading(prev => ({ ...prev, payment: true }));

        try {
            // Handle zero amount payment (100% discount case)
            if (grandTotal === 0) {
                const transactionId = `free_${uuidv4().substring(0, 8)}`;

                await Promise.all([
                    axios.post(`${base_url}/api/v1/transaction`, {
                        merchant_id,
                        transactionType: 'credit',
                        transaction_id: transactionId,
                        amount: 0,
                        info: {
                            base_price: leadAmount,
                            cgst,
                            sgst,
                            sub_total: leadAmount + cgst + sgst,
                            discount: discountAmount,
                            grand_total: 0,
                            // note: "Free payment with 100% discount"
                        },
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }),
                    code && axios.post(`${base_url}/api/v1/promo-code-histories`, {
                        merchant_id,
                        promo_code_id: promoCode.id,
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }),
                    axios.post(`${base_url}/api/v1/packages/payment/status`, {
                        id: currentRequestId
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    })
                ]);

                setCurrentPaymentStatus(true);
                await fetchPackageList();
                toast.success('Payment processed successfully with 100% discount');
                closeContactModal();
                return;
            }

            // Normal payment flow for non-zero amounts
            const orderId = uuidv4();
            const paymentReq = {
                order_id: orderId,
                amount: grandTotal,
                currency: "INR",
                payment_capture: 1
            };

            const result = await axios.post(`${base_url}/api/v1/razorpay/create`, paymentReq, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const orderData = result?.data?.data;
            if (!orderData) {
                throw new Error('Payment initialization failed');
            }
            const options = {
                key: import.meta.env.VITE_RAZORPAY_KEY || "rzp_live_6XXCRghG3I98Mm",
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Event Planet",
                description: "Venue Booking Payment",
                image: "https://eventplanet.in/assets/home/logo_main.png",
                order_id: orderData.id,
                handler: async (response) => {
                    try {
                        await Promise.all([
                            axios.post(`${base_url}/api/v1/transaction`, {
                                merchant_id,
                                transactionType: 'credit',
                                transaction_id: response?.razorpay_payment_id,
                                amount: grandTotal,
                                info: {
                                    base_price: leadAmount,
                                    cgst,
                                    sgst,
                                    sub_total: leadAmount + cgst + sgst,
                                    discount: discountAmount,
                                    grand_total: grandTotal
                                },
                            }, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                }
                            }),
                            code && axios.post(`${base_url}/api/v1/promo-code-histories`, {
                                merchant_id,
                                promo_code_id: promoCode.id,
                            }, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                }
                            }),
                            axios.post(`${base_url}/api/v1/packages/payment/status`, {
                                id: currentRequestId
                            }, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                }
                            })
                        ]);
                        closeContactModal();
                        setCurrentPaymentStatus(true);
                        await fetchPackageList();
                        toast.success('Payment successful!');
                    } catch (error) {
                        console.error('Payment processing error:', error);
                        toast.error('Error processing payment');
                    }
                },
                theme: { color: "#3399cc" },
                prefill: {
                    name: merchant.name || '',
                    email: merchant.email || '',
                    contact: merchant.phone || ''
                },
                modal: {
                    ondismiss: () => {
                        toast.info('Payment window closed');
                    }
                }
            };

            const paymentObj = new window.Razorpay(options);
            paymentObj.open();
        } catch (error) {
            toast.error(`Payment error: ${error.message}`);
        } finally {
            setLoading(prev => ({ ...prev, payment: false }));
        }
    }, [base_url, merchant_id, grandTotal, leadAmount, cgst, sgst, discountAmount, code, promoCode, currentRequestId, merchant, fetchPackageList]);
    // Modal handlers
    const showContactDetails = (item) => {
        console.log(item)
        setCurrentContact(item);
        setCurrentRequestId(item.id);
        setCurrentPaymentStatus(item.payment_status);
        setShowContactModal(true);
    };

    const closeContactModal = () => {
        setShowContactModal(false);
        setCurrentContact({});
        setCurrentRequestId(null);
        setCode('');
        setDiscountAmount(0);
        setPromoCode({});
    };

    const dealCloseTrigger = () => {
        fetchPackageList();//bad practice
    }

    const inputCodeHandler = () => {
        applyPromoCode(inputCode)
    };
    return (
        <div className="page-wrapper">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className='card-header d-flex justify-content-between align-items-center' style={{ backgroundColor: '#085f9a' }}>
                                <h4 className='text-white mb-0'>Package Enquiries</h4>
                                <div className='w-25'>
                                    <input
                                        type="text"
                                        placeholder='Search Enquiry'
                                        className='form-control'
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className='card-body'>
                                <div className='table-responsive'>
                                    <table className='table table-bordered shadow-sm mt-3' cellPadding={5}>
                                        <thead className=''>
                                            <tr>
                                                <th>S. No.</th>
                                                <th>Package Name</th>
                                                <th>Venue Name</th>
                                                <th>Customer Name</th>
                                                <th>Function Date</th>
                                                <th>Mobile No</th>
                                                <th>Enquiry Date</th>
                                                <th>Payment Status</th>
                                                <th>Lead Payment</th>
                                                <th>Deal Closed At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading.table ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center">
                                                        <Spinner animation="border" variant="primary" />
                                                    </td>
                                                </tr>
                                            ) : filteredPackages.length > 0 ? (
                                                filteredPackages.map((item, index) => (
                                                    <tr key={item.id || index}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.package_name || 'N/A'}</td>
                                                        <td>{item.venue_name || 'N/A'}</td>
                                                        <td>{item.name || 'N/A'}</td>
                                                        <td>{item.function_date ? dayjs(item.function_date).format("DD MMMM, YYYY") : 'N/A'}</td>
                                                        <td>{item.payment_status ? item.mobno : maskString(item.mobno)}</td>
                                                        <td>{item.created_at ? dayjs(item.created_at).format("DD MMMM, YYYY") : 'N/A'}</td>
                                                        <td>
                                                            <span className={`badge ${item.payment_status ? 'bg-success' : 'bg-warning'}`}>
                                                                {item.payment_status ? 'Paid' : 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {
                                                                item.payment_status ? (
                                                                    <Button
                                                                        variant={item.payment_status ? "outline-success" : "outline-primary"}
                                                                        size="sm" disabled={loading.payment}
                                                                    >
                                                                        Done
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        variant={item.payment_status ? "outline-success" : "outline-primary"}
                                                                        size="sm"
                                                                        onClick={() => showContactDetails(item)}
                                                                        disabled={loading.payment}
                                                                    >
                                                                        {'Pay Now'}
                                                                        {loading.payment && <Spinner animation="border" size="sm" className="ms-2" />}
                                                                    </Button>
                                                                )
                                                            }

                                                        </td>
                                                        <td>
                                                            {
                                                                item.payment_status ? (
                                                                    <PackageDeal dealCloseTrigger={dealCloseTrigger} item={item} />
                                                                ) : (
                                                                    <center>---</center>
                                                                )
                                                            }

                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={9} className="text-center">No records found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={showContactModal} onHide={closeContactModal} backdrop="static" centered dialogClassName="modal-90w"
                aria-labelledby="example-custom-modal-styling-title">
                <Modal.Header closeButton>
                    <Modal.Title>Package Enquiry Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        {!currentPaymentStatus && (
                            <div className="col-md-12">
                                <div className="payment-section p-3 border rounded">
                                    <h5>Payment Details</h5>
                                    <PaymentBreakdown
                                        leadAmount={leadAmount}
                                        cgst={cgst}
                                        sgst={sgst}
                                        discountAmount={discountAmount}
                                        grandTotal={grandTotal}
                                    />
                                    <div className="row my-2">
                                        <div className="col-md-9">
                                            <div className="form-froup">
                                                <input type="text" className="form-control" placeholder='Promo code' value={inputCode} onChange={(e) => setInputCode(e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <button className='btn btn-sm btn-primary text-light' onClick={inputCodeHandler} disabled={!inputCode}>Apply</button>
                                        </div>
                                    </div>
                                    <PromoCodeSection
                                        promoCodeList={promoCodeList}
                                        currentCode={code}
                                        onApply={applyPromoCode}
                                        loading={loading.promoCode}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {!currentPaymentStatus ? (
                        <Button
                            variant="success"
                            onClick={processPayment}
                            disabled={loading.payment}
                            className="d-flex align-items-center"
                        >
                            {loading.payment ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Processing...
                                </>
                            ) : (
                                `Pay ₹${grandTotal.toFixed(2)}`
                            )}
                        </Button>
                    ) : (
                        <Button variant="secondary" onClick={closeContactModal}>
                            Close
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

// Sub-components
const PaymentBreakdown = ({ leadAmount, cgst, sgst, discountAmount, grandTotal }) => (
    <div className="payment-breakdown">
        <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">Base Price:</span>
            <span>₹{leadAmount?.toFixed(2)}</span>
        </div>
        <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">CGST (9%):</span>
            <span>₹{cgst.toFixed(2)}</span>
        </div>
        <div className="d-flex justify-content-between mb-2">
            <span className="text-muted">SGST (9%):</span>
            <span>₹{sgst.toFixed(2)}</span>
        </div>
        <div className="d-flex justify-content-between mb-2 border-bottom pb-2">
            <span className="text-muted">Sub Amount:</span>
            <span>₹{(leadAmount + cgst + sgst).toFixed(2)}</span>
        </div>
        {discountAmount > 0 && (
            <div className="d-flex justify-content-between mb-2 text-success">
                <span>Discount Amount:</span>
                <span>-₹{discountAmount.toFixed(2)}</span>
            </div>
        )}
        <div className="d-flex justify-content-between mt-3 fw-bold fs-5">
            <span>Grand Total:</span>
            <span>₹{grandTotal.toFixed(2)}</span>
        </div>
    </div>
);

const PromoCodeSection = ({ promoCodeList, currentCode, onApply, loading }) => (
    <div className="promo-code-section mt-4">
        <h6 className="mb-3">Apply Promo Code</h6>
        <div className="promo-codes-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {promoCodeList.length > 0 ? (
                promoCodeList.map((item) => (
                    <div className='promo-card mb-2 p-2 border rounded' key={item.id}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <span className="badge bg-primary me-2">{item.code}</span>
                                <strong>{item.value}% Discount</strong>
                            </div>
                            <button
                                className={`btn btn-sm ${item.code === currentCode ? 'btn-success' : 'btn-outline-primary'}`}
                                onClick={() => !loading && onApply(item.code)}
                                disabled={loading}
                            >
                                {item.code === currentCode ? 'Applied' : 'Apply'}
                                {loading && item.code === currentCode && (
                                    <Spinner animation="border" size="sm" className="ms-2" />
                                )}
                            </button>
                        </div>
                        {item.description && (
                            <div className="promo-desc mt-1">
                                <small className="text-muted">{item.description}</small>
                            </div>
                        )}
                    </div>
                ))
            ) : (
                <div className="text-center text-muted py-2">
                    No promo codes available
                </div>
            )}
        </div>
    </div>
);

export default PackageEnquiry;